import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ResponseModel, { PaginationRes } from "../../../models/response.model";
import {
  approveFloorInwardRequestApi,
  cancelFloorInwardRequestApi,
  createFloorInwardRequestApi,
  finalizeOutwardMaterialsApi,
  finalizeOutwardProductsApi,
  getFloorBatchesApi,
  getFloorRequestByIdApi,
  getFloorRequestsApi,
  getFloorStocksApi,
  getRequestOutwardBatchesApi,
  outwardMaterialsApi,
  outwardProductsApi,
  outwardProductsExcelApi,
  updateFloorRequestItemsApi,
} from "../../../api/Factory/floor.api";
import { UnitType } from "../../../models/warehouseRawMaterial.model";
import { IFloorRequest, IFloorRequestItem } from "../../../models/floor.model";
import { ProductBatchesResponse } from "../../../models/productOutwardBatches.model";
import { MaterialBatchesResponse } from "../../../models/materialOutwardBatches.model";

interface StockRequest {
  requestNo: number;
  batchNo: number | null;
  date: string;
  requestedBy: string;
  quantity: number;
  unit: string;
}

interface MaterialStock {
  rawMaterialId: string;
  materialName: string;
  totalQuantity: number;
  unit: string;
  requests: StockRequest[];
}

interface NewFloorState {
  requests: IFloorRequest[];
  batches: IFloorRequest[];
  productOutwardBatches?: ProductBatchesResponse;
  materialOutwardBatches?: MaterialBatchesResponse;
  allFloorItems: MaterialStock[];
  selectedRequestitems?: IFloorRequestItem[];
  pagination: {
    getFloorRequestsPagination?: PaginationRes;
    getFloorBatchesPagination?: PaginationRes;
    getFloorStockPagination?: PaginationRes;
  };
  loading: {
    getFloorRequestsLoading: boolean;
    getFloorRequestByIdLoading: boolean;
    createFloorInwardRequestLoading: boolean;
    approveFloorInwardRequestLoading: boolean;
    cancelFloorInwardRequestLoading: boolean;
    updateFloorRequestItemsLoading: boolean;
    getFloorBatchesLoading: boolean;
    getFloorStockLoading: boolean;
    outwardProductsloading: boolean;
    outwardMaterialsloading: boolean;
    outwardProductsExcelLoading: boolean;
    finalizeOutwardMaterialsLoading: boolean;
    finalizeOutwardProductsLoading: boolean;
    getRequestOutwardBatchesLoading: boolean;
    getReplaceReturnRequestsLoading: boolean;
    replaceReturnRequestLoading: boolean;
    cancelReplaceReturnRequestLoading: boolean;
  };
}

const initialState: NewFloorState = {
  requests: [],
  batches: [],
  productOutwardBatches: undefined,
  materialOutwardBatches: undefined,
  allFloorItems: [],
  pagination: {},
  loading: {
    getFloorRequestsLoading: false,
    getFloorRequestByIdLoading: false,
    createFloorInwardRequestLoading: false,
    approveFloorInwardRequestLoading: false,
    cancelFloorInwardRequestLoading: false,
    updateFloorRequestItemsLoading: false,
    getFloorBatchesLoading: false,
    getFloorStockLoading: false,
    outwardProductsloading: false,
    outwardMaterialsloading: false,
    outwardProductsExcelLoading: false,
    finalizeOutwardMaterialsLoading: false,
    finalizeOutwardProductsLoading: false,
    getRequestOutwardBatchesLoading: false,
    getReplaceReturnRequestsLoading: false,
    replaceReturnRequestLoading: false,
    cancelReplaceReturnRequestLoading: false,
  },
};

export const getFloorRequests = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      status: string;
      requestedBy?: string;
      warehouseId?: string;
    };
  },
  { rejectValue: string }
>("newFloor/getFloorRequests", async ({ params }, { rejectWithValue }) => {
  try {
    return await getFloorRequestsApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const getFloorRequestById = createAsyncThunk<
  ResponseModel,
  {
    id: string;
    materialSearch?: string;
  },
  { rejectValue: string }
>(
  "newFloor/getFloorRequestById",
  async ({ id, materialSearch }, { rejectWithValue }) => {
    try {
      return await getFloorRequestByIdApi(id, materialSearch);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch report",
      );
    }
  },
);

export const createFloorInwardRequest = createAsyncThunk<
  ResponseModel,
  {
    items: {
      productId: string;
      quantity: number;
      unit: UnitType;
      type: string;
      remark?: string;
    }[];
  },
  { rejectValue: string }
>(
  "newFloor/createFloorInwardRequest",
  async ({ items }, { rejectWithValue }) => {
    try {
      return await createFloorInwardRequestApi(items);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create floor inward requests",
      );
    }
  },
);

export const approveFloorInwardRequest = createAsyncThunk<
  ResponseModel,
  {
    id: string;
    data: {
      requestItemId: string;
      approvedQuantity: number;
      unit: string;
    }[];
  },
  { rejectValue: string }
>(
  "newFloor/approveFloorInwardRequest",
  async ({ data, id }, { rejectWithValue }) => {
    try {
      return await approveFloorInwardRequestApi(id, data);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create floor inward requests",
      );
    }
  },
);

export const cancelFloorInwardRequest = createAsyncThunk<
  ResponseModel,
  {
    id: string;
  },
  { rejectValue: string }
>("newFloor/cancelFloorInwardRequest", async ({ id }, { rejectWithValue }) => {
  try {
    return await cancelFloorInwardRequestApi(id);
  } catch (err: any) {
    return rejectWithValue(
      err.message || "Failed to create floor inward requests",
    );
  }
});

export const updateFloorRequestItems = createAsyncThunk<
  ResponseModel,
  {
    id: string;
    data: any[];
  },
  { rejectValue: string }
>(
  "newFloor/updateFloorRequestItems",
  async ({ data, id }, { rejectWithValue }) => {
    try {
      return await updateFloorRequestItemsApi(id, data);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create floor inward requests",
      );
    }
  },
);

export const getFloorBatches = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      status?: string;
      search?: string;
      requestedBy?: string;
      warehouseId?: string;
    };
  },
  { rejectValue: string }
>("newFloor/getFloorBatches", async ({ params }, { rejectWithValue }) => {
  try {
    return await getFloorBatchesApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const getFloorStock = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      search?: string;
      requestedBy?: string;
      warehouseId?: string;
    };
  },
  { rejectValue: string }
>("newFloor/getFloorStock", async ({ params }, { rejectWithValue }) => {
  try {
    return await getFloorStocksApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const outwardProducts = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    units: {
      serialNumber: string;
      loggerSerialNumber: string;
      manufacturingSerialNumber: string;
      productId: string;
    }[];
  },
  { rejectValue: string }
>(
  "newFloor/outwardProducts",
  async ({ requestId, units }, { rejectWithValue }) => {
    try {
      return await outwardProductsApi({ requestId, units });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create floor inward requests",
      );
    }
  },
);

export const outwardProductsExcel = createAsyncThunk<
  ResponseModel,
  {
    batchNo: string | number;
    file: File;
  },
  { rejectValue: string }
>(
  "newFloor/outwardProductsExcel",
  async ({ batchNo, file }, { rejectWithValue }) => {
    try {
      return await outwardProductsExcelApi({ batchNo, file });
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to upload outward excel",
      );
    }
  },
);

export const outwardMaterials = createAsyncThunk<
  ResponseModel,
  {
    materials: {
      requestId: string;
      materialId: string;
      quantity: number;
      unit: string;
    }[];
  },
  { rejectValue: string }
>("newFloor/outwardMaterials", async ({ materials }, { rejectWithValue }) => {
  try {
    return await outwardMaterialsApi(materials);
  } catch (err: any) {
    return rejectWithValue(
      err.message || "Failed to create floor inward requests",
    );
  }
});

export const finalizeOutwardMaterials = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
  },
  { rejectValue: string }
>(
  "newFloor/finalizeOutwardMaterials",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      return await finalizeOutwardMaterialsApi(requestId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to finalize outward materials",
      );
    }
  },
);

export const finalizeOutwardProducts = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
  },
  { rejectValue: string }
>(
  "newFloor/finalizeOutwardProducts",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      return await finalizeOutwardProductsApi({ requestId });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to finalize outward products",
      );
    }
  },
);

export const getRequestOutwardBatches = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
  },
  { rejectValue: string }
>(
  "newFloor/getRequestOutwardBatches",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      return await getRequestOutwardBatchesApi(requestId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to get outward batches for request",
      );
    }
  },
);

const newFloorSlice = createSlice({
  name: "newFloor",
  initialState,
  reducers: {
    clearSelectedRequestItems: (state) => {
      state.selectedRequestitems = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFloorRequests.pending, (state) => {
        state.loading.getFloorRequestsLoading = true;
      })
      .addCase(getFloorRequests.fulfilled, (state, action) => {
        state.loading.getFloorRequestsLoading = false;
        state.requests = action.payload.data;
        state.pagination.getFloorRequestsPagination = action.payload.pagination;
      })
      .addCase(getFloorRequests.rejected, (state, action) => {
        state.loading.getFloorRequestsLoading = false;
      })
      .addCase(getFloorBatches.pending, (state) => {
        state.loading.getFloorBatchesLoading = true;
      })
      .addCase(getFloorBatches.fulfilled, (state, action) => {
        state.loading.getFloorBatchesLoading = false;
        state.batches = action.payload.data;
        state.pagination.getFloorBatchesPagination = action.payload.pagination;
      })
      .addCase(getFloorBatches.rejected, (state, action) => {
        state.loading.getFloorBatchesLoading = false;
      })
      .addCase(getFloorStock.pending, (state) => {
        state.loading.getFloorStockLoading = true;
      })
      .addCase(getFloorStock.fulfilled, (state, action) => {
        state.loading.getFloorStockLoading = false;
        state.allFloorItems = action.payload.data;
        state.pagination.getFloorBatchesPagination = action.payload.pagination;
      })
      .addCase(getFloorStock.rejected, (state, action) => {
        state.loading.getFloorStockLoading = false;
      })
      .addCase(getFloorRequestById.pending, (state) => {
        state.loading.getFloorRequestByIdLoading = true;
      })
      .addCase(getFloorRequestById.fulfilled, (state, action) => {
        state.loading.getFloorRequestByIdLoading = false;
        state.selectedRequestitems = action.payload.data;
      })
      .addCase(getFloorRequestById.rejected, (state, action) => {
        state.loading.getFloorRequestByIdLoading = false;
      })
      .addCase(createFloorInwardRequest.pending, (state) => {
        state.loading.createFloorInwardRequestLoading = true;
      })
      .addCase(createFloorInwardRequest.fulfilled, (state, action) => {
        state.loading.createFloorInwardRequestLoading = false;
      })
      .addCase(createFloorInwardRequest.rejected, (state, action) => {
        state.loading.createFloorInwardRequestLoading = false;
      })
      .addCase(approveFloorInwardRequest.pending, (state) => {
        state.loading.approveFloorInwardRequestLoading = true;
      })
      .addCase(approveFloorInwardRequest.fulfilled, (state, action) => {
        state.loading.approveFloorInwardRequestLoading = false;
      })
      .addCase(approveFloorInwardRequest.rejected, (state, action) => {
        state.loading.approveFloorInwardRequestLoading = false;
      })
      .addCase(cancelFloorInwardRequest.pending, (state) => {
        state.loading.cancelFloorInwardRequestLoading = true;
      })
      .addCase(cancelFloorInwardRequest.fulfilled, (state, action) => {
        state.loading.cancelFloorInwardRequestLoading = false;
      })
      .addCase(cancelFloorInwardRequest.rejected, (state, action) => {
        state.loading.cancelFloorInwardRequestLoading = false;
      })
      .addCase(updateFloorRequestItems.pending, (state) => {
        state.loading.updateFloorRequestItemsLoading = true;
      })
      .addCase(updateFloorRequestItems.fulfilled, (state, action) => {
        state.loading.updateFloorRequestItemsLoading = false;
      })
      .addCase(updateFloorRequestItems.rejected, (state, action) => {
        state.loading.updateFloorRequestItemsLoading = false;
      })
      .addCase(outwardProducts.pending, (state) => {
        state.loading.outwardProductsloading = true;
      })
      .addCase(outwardProducts.fulfilled, (state, action) => {
        state.loading.outwardProductsloading = false;
      })
      .addCase(outwardProducts.rejected, (state, action) => {
        state.loading.outwardProductsloading = false;
      })
      .addCase(outwardProductsExcel.pending, (state) => {
        state.loading.outwardProductsExcelLoading = true;
      })
      .addCase(outwardProductsExcel.fulfilled, (state, action) => {
        state.loading.outwardProductsExcelLoading = false;
      })
      .addCase(outwardProductsExcel.rejected, (state, action) => {
        state.loading.outwardProductsExcelLoading = false;
      })
      .addCase(finalizeOutwardMaterials.pending, (state) => {
        state.loading.finalizeOutwardMaterialsLoading = true;
      })
      .addCase(finalizeOutwardMaterials.fulfilled, (state, action) => {
        state.loading.finalizeOutwardMaterialsLoading = false;
      })
      .addCase(finalizeOutwardMaterials.rejected, (state, action) => {
        state.loading.finalizeOutwardMaterialsLoading = false;
      })
      .addCase(finalizeOutwardProducts.pending, (state) => {
        state.loading.finalizeOutwardProductsLoading = true;
      })
      .addCase(finalizeOutwardProducts.fulfilled, (state, action) => {
        state.loading.finalizeOutwardProductsLoading = false;
      })
      .addCase(finalizeOutwardProducts.rejected, (state, action) => {
        state.loading.finalizeOutwardProductsLoading = false;
      })
      .addCase(getRequestOutwardBatches.pending, (state) => {
        state.loading.getRequestOutwardBatchesLoading = true;
      })
      .addCase(getRequestOutwardBatches.fulfilled, (state, action) => {
        state.loading.getRequestOutwardBatchesLoading = false;
        const data = action.payload.data;
        state.productOutwardBatches = data.productBatches;
        state.materialOutwardBatches = data.materialBatches;
      })
      .addCase(getRequestOutwardBatches.rejected, (state, action) => {
        state.loading.getRequestOutwardBatchesLoading = false;
      });
  },
});

export const { clearSelectedRequestItems } = newFloorSlice.actions;
export default newFloorSlice.reducer;
